import cn from 'classnames';
import dayjs from 'dayjs';
import { FC, useMemo } from 'react';
import { Input } from '../../../../../components/input/input';
import selectS from '../../../../../components/select/select.module.css';
import { DatePicker } from '../../../../../features/date-time/date-picker/date-picker';
import { Tooltip } from '../../../../../features/tooltip/tooltip';
import tooltipS from '../../../../../features/tooltip/tooltip.module.css';
import { useLang } from '../../../../../hooks/use-lang';
import { useToggle } from '../../../../../hooks/use-toggle';
import {
  EXTERNAL_MONITORING_PERIOD_OPTIONS,
  boundsFromLocalDateRange,
  buildExternalMonitoringDashboardPresetMonths,
  isExternalMonitoringDashboardPresetActive,
} from '../../../../../helpers/external-monitoring-period';
import s from './dashboard-external-monitoring.module.css';

type Props = {
  periodBeginSec: number;
  periodEndSec: number;
  onRangeChange: (beginSec: number, endSec: number) => void;
};

export const ExternalMonitoringPeriodDropdown: FC<Props> = ({
  periodBeginSec,
  periodEndSec,
  onRangeChange,
}) => {
  const { t } = useLang();
  const [open, , setOpen] = useToggle();

  const display =
    `${dayjs.unix(periodBeginSec).format('DD.MM.YYYY')} — ${dayjs.unix(periodEndSec).format('DD.MM.YYYY')}`;

  /** Локальный календарный день — совпадает с тем, что уходит в запрос после boundsFromLocalDateRange. */
  const fromDate = useMemo(() => {
    const d = new Date(periodBeginSec * 1000);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }, [periodBeginSec]);

  const toDate = useMemo(() => {
    const d = new Date(periodEndSec * 1000);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }, [periodEndSec]);

  const today = new Date();

  const applyRange = (from: Date, to: Date) => {
    const { begin, end } = boundsFromLocalDateRange(from, to);
    onRangeChange(begin, end);
  };

  const onFromChange = (d?: Date) => {
    if (!d) return;
    applyRange(d, toDate);
  };

  const onToChange = (d?: Date) => {
    if (!d) return;
    applyRange(fromDate, d);
  };

  const onPresetMonths = (months: number) => {
    const { begin, end } = buildExternalMonitoringDashboardPresetMonths(months);
    onRangeChange(begin, end);
    setOpen(false);
  };

  return (
    <Tooltip
      behavior="click"
      closeOnContentClick={false}
      open={open}
      onOpen={setOpen}
      triggerWrapperClassName={cn(selectS.component, selectS.lg, s.periodDropdownTriggerWrap)}
      optionsWrapperClassName={cn(tooltipS.list, tooltipS.click, s.periodDropdownPanel)}
      trigger={
        <Input
          readOnly
          value={display}
          placeholder={t('Период')}
          className={selectS.input}
          tabIndex={-1}
        />
      }
    >
      <div className={s.periodDropdownBody}>
        <div className={s.periodDropdownPresets}>
          {EXTERNAL_MONITORING_PERIOD_OPTIONS.map((opt) => {
            const months = Number(opt.id);
            const active = isExternalMonitoringDashboardPresetActive(
              periodBeginSec,
              periodEndSec,
              months,
            );
            return (
              <button
                key={opt.id}
                type="button"
                className={cn(s.periodPresetBtn, active && s.periodPresetBtnActive)}
                onClick={() => onPresetMonths(months)}
              >
                {t(opt.value)}
              </button>
            );
          })}
        </div>
        <div className={s.periodDropdownDates}>
          <DatePicker
            className={s.periodDropdownDatePicker}
            selectedDate={fromDate}
            onSelectDate={onFromChange}
            placeholder={t('От')}
            maxDate={today}
          />
          <DatePicker
            className={s.periodDropdownDatePicker}
            selectedDate={toDate}
            onSelectDate={onToChange}
            placeholder={t('До')}
            maxDate={today}
            minDate={fromDate}
          />
        </div>
      </div>
    </Tooltip>
  );
};
